import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Interceptor para agregar token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authService = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  logout: () => api.post('/auth/logout')
};

// Dashboard
export const dashboardService = {
  getDatos: () => api.get('/dashboard')
};

// Departamentos
export const departamentosService = {
  getAll: () => api.get('/departamentos'),
  getById: (id) => api.get(`/departamentos/${id}`),
  create: (data) => api.post('/departamentos', data),
  update: (id, data) => api.put(`/departamentos/${id}`, data),
  delete: (id) => api.delete(`/departamentos/${id}`),
  getEstadoCuenta: (id) => api.get(`/departamentos/${id}/estado-cuenta`),
  gestionarSaldo: (id, data) => api.post(`/departamentos/${id}/saldo-favor`, data),
  getMovimientosSaldo: (id) => api.get(`/departamentos/${id}/movimientos-saldo`),
  pagarConSaldo: (id, data) => api.post(`/departamentos/${id}/pagar-con-saldo`, data)
};

// Ingresos
export const ingresosService = {
  getAll: (params) => api.get('/ingresos', { params }),
  getById: (id) => api.get(`/ingresos/${id}`),
  create: (data) => api.post('/ingresos', data),
  update: (id, data) => api.put(`/ingresos/${id}`, data),
  delete: (id) => api.delete(`/ingresos/${id}`),
  getResumenMensual: (anio) => api.get('/ingresos/resumen/mensual', { params: { anio } })
};

// Egresos
export const egresosService = {
  getAll: (params) => api.get('/egresos', { params }),
  getById: (id) => api.get(`/egresos/${id}`),
  create: (data) => api.post('/egresos', data),
  update: (id, data) => api.put(`/egresos/${id}`, data),
  delete: (id) => api.delete(`/egresos/${id}`),
  getResumenPorCategoria: (params) => api.get('/egresos/resumen/por-categoria', { params }),
  marcarPagado: (id, data) => api.put(`/egresos/${id}/marcar-pagado`, data),
  getHistoricoServicios: (params) => api.get('/egresos/historico/servicios', { params })
};

// Flujo de Caja
export const flujoCajaService = {
  getAll: (params) => api.get('/flujo-caja', { params }),
  getSaldoActual: () => api.get('/flujo-caja/saldo-actual'),
  getResumenMensual: (params) => api.get('/flujo-caja/resumen-mensual', { params })
};

// Balances
export const balancesService = {
  getAll: (params) => api.get('/balances', { params }),
  getByMesAnio: (mes, anio) => api.get(`/balances/${mes}/${anio}`),
  save: (data) => api.post('/balances', data),
  aprobar: (id) => api.put(`/balances/${id}/aprobar`),
  getResumenAnual: (anio) => api.get(`/balances/resumen/anual/${anio}`),
  generarPDF: (mes, anio) => api.get(`/balances/pdf/${mes}/${anio}`),
  getConfiguracion: () => api.get('/balances/configuracion'),
  saveConfiguracion: (data) => api.post('/balances/configuracion', data)
};

// Proveedores
export const proveedoresService = {
  getAll: () => api.get('/proveedores'),
  getById: (id) => api.get(`/proveedores/${id}`),
  create: (data) => api.post('/proveedores', data),
  update: (id, data) => api.put(`/proveedores/${id}`, data),
  delete: (id) => api.delete(`/proveedores/${id}`)
};

// Personal
export const personalService = {
  getAll: () => api.get('/personal'),
  getById: (id) => api.get(`/personal/${id}`),
  create: (data) => api.post('/personal', data),
  update: (id, data) => api.put(`/personal/${id}`, data),
  delete: (id) => api.delete(`/personal/${id}`)
};

// Equipos
export const equiposService = {
  getAll: (params) => api.get('/equipos', { params }),
  getById: (id) => api.get(`/equipos/${id}`),
  create: (data) => api.post('/equipos', data),
  update: (id, data) => api.put(`/equipos/${id}`, data),
  delete: (id) => api.delete(`/equipos/${id}`),
  addMantenimiento: (id, data) => api.post(`/equipos/${id}/mantenimiento`, data),
  getAlertasMantenimiento: () => api.get('/equipos/alertas/mantenimiento')
};

// Áreas Comunes
export const areasService = {
  getAll: () => api.get('/areas'),
  getById: (id) => api.get(`/areas/${id}`),
  create: (data) => api.post('/areas', data),
  update: (id, data) => api.put(`/areas/${id}`, data),
  delete: (id) => api.delete(`/areas/${id}`)
};

// Reservas
export const reservasService = {
  getAll: (params) => api.get('/reservas', { params }),
  create: (data) => api.post('/reservas', data),
  aprobar: (id) => api.put(`/reservas/${id}/aprobar`),
  cancelar: (id) => api.put(`/reservas/${id}/cancelar`)
};

// Comunicados
export const comunicadosService = {
  getAll: (params) => api.get('/comunicados', { params }),
  getById: (id) => api.get(`/comunicados/${id}`),
  create: (data) => api.post('/comunicados', data),
  update: (id, data) => api.put(`/comunicados/${id}`, data),
  delete: (id) => api.delete(`/comunicados/${id}`)
};

// Votaciones
export const votacionesService = {
  getAll: (params) => api.get('/votaciones', { params }),
  getById: (id) => api.get(`/votaciones/${id}`),
  create: (data) => api.post('/votaciones', data),
  votar: (id, opcion) => api.post(`/votaciones/${id}/votar`, { opcion }),
  finalizar: (id) => api.put(`/votaciones/${id}/finalizar`)
};

// Documentos
export const documentosService = {
  getAll: (params) => api.get('/documentos', { params }),
  getById: (id) => api.get(`/documentos/${id}`),
  upload: (formData) => api.post('/documentos', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (id) => api.get(`/documentos/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/documentos/${id}`)
};

// Visitantes
export const visitantesService = {
  getAll: (params) => api.get('/visitantes', { params }),
  create: (data) => api.post('/visitantes', data),
  registrarSalida: (id) => api.put(`/visitantes/${id}/salida`),
  getEntregas: (params) => api.get('/visitantes/entregas', { params }),
  createEntrega: (data) => api.post('/visitantes/entregas', data),
  entregarPaquete: (id, data) => api.put(`/visitantes/entregas/${id}/entregar`, data)
};

// Incidencias
export const incidenciasService = {
  getAll: (params) => api.get('/incidencias', { params }),
  getById: (id) => api.get(`/incidencias/${id}`),
  create: (data) => api.post('/incidencias', data),
  update: (id, data) => api.put(`/incidencias/${id}`, data),
  atender: (id) => api.put(`/incidencias/${id}/atender`),
  resolver: (id, solucion) => api.put(`/incidencias/${id}/resolver`, { solucion }),
  delete: (id) => api.delete(`/incidencias/${id}`)
};

// Usuarios
export const usuariosService = {
  getAll: () => api.get('/usuarios'),
  getById: (id) => api.get(`/usuarios/${id}`),
  create: (data) => api.post('/usuarios', data),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`)
};

// Sanciones
export const sancionesService = {
  getAll: (params) => api.get('/sanciones', { params }),
  getById: (id) => api.get(`/sanciones/${id}`),
  getByDepartamento: (departamentoId) => api.get(`/sanciones/departamento/${departamentoId}`),
  getResumen: (params) => api.get('/sanciones/resumen', { params }),
  create: (data) => api.post('/sanciones', data),
  update: (id, data) => api.put(`/sanciones/${id}`, data),
  marcarPagada: (id, data) => api.put(`/sanciones/${id}/pagar`, data),
  anular: (id, data) => api.put(`/sanciones/${id}/anular`, data),
  apelar: (id, data) => api.put(`/sanciones/${id}/apelar`, data),
  delete: (id) => api.delete(`/sanciones/${id}`)
};

// Recibos
export const recibosService = {
  getAll: (params) => api.get('/recibos', { params }),
  getById: (id) => api.get(`/recibos/${id}`),
  getMiCuenta: () => api.get('/recibos/mi-cuenta'),
  generarCuotas: (data) => api.post('/recibos/generar', data),
  generarAdelantados: (data) => api.post('/recibos/generar-adelantados', data),
  create: (data) => api.post('/recibos', data),
  update: (id, data) => api.put(`/recibos/${id}`, data),
  anular: (id) => api.put(`/recibos/${id}/anular`),
  delete: (id) => api.delete(`/recibos/${id}`)
};

// Pagos Propietario
export const pagosPropietarioService = {
  getAll: (params) => api.get('/pagos-propietario', { params }),
  getById: (id) => api.get(`/pagos-propietario/${id}`),
  getResumen: () => api.get('/pagos-propietario/resumen'),
  create: (data) => api.post('/pagos-propietario', data),
  confirmar: (id, data = {}) => api.put(`/pagos-propietario/${id}/confirmar`, data),
  rechazar: (id, data) => api.put(`/pagos-propietario/${id}/rechazar`, data),
  delete: (id) => api.delete(`/pagos-propietario/${id}`)
};

export default api;